import { useMemo, type ReactNode } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import ThemeProvider from "@/app/providers/ThemeProvider";
import { LanguageProvider } from "@/app/providers/LanguageProvider";
import { EditorAuthProvider, type TokenManager } from "@/auth";
import { createStudioQueryClient } from "./queryClient";
import { StudioCurrencyProvider } from "./StudioCurrencyProvider";
import { SessionExpiredGate } from "./SessionExpiredGate";

/**
 * The Studio's provider stack. Order matters: Theme owns the antd
 * ConfigProvider tokens every keep-list component styles against; the auth
 * provider wraps everything that talks to the API so the gate can replace
 * the whole editor when the session latches. Currency sits INSIDE the auth
 * gate — it reads /context (a tokened query) and must not fire on a
 * hostless direct visit, where no token can ever exist.
 */
export function StudioProviders({
  manager,
  children,
}: {
  manager: TokenManager;
  children: ReactNode;
}) {
  const queryClient = useMemo(() => createStudioQueryClient(), []);
  return (
    <ThemeProvider>
      <LanguageProvider>
        <QueryClientProvider client={queryClient}>
          <HelmetProvider>
            <SessionExpiredGate>
              <EditorAuthProvider manager={manager}>
                <StudioCurrencyProvider>{children}</StudioCurrencyProvider>
              </EditorAuthProvider>
            </SessionExpiredGate>
          </HelmetProvider>
        </QueryClientProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}
